import React, { useEffect, useRef, useState } from 'react';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import { Button, Form, Modal } from '../../../components/ui';
import {
  CatalogRequestError,
  materialCatalogService,
  type CatalogMaterial,
  type CatalogOptions,
  type MaterialField,
  type MaterialPayload,
} from '../../../services/materialCatalogService';

interface MaterialFormModalProps {
  show: boolean;
  /** The material being corrected; omit to add a new one. */
  material?: CatalogMaterial | null;
  options: CatalogOptions | null;
  onHide: () => void;
  onSaved: (material: CatalogMaterial) => void;
}

type Errors = Partial<Record<MaterialField, string>>;

const EMPTY: MaterialPayload = {
  code: '',
  description: '',
  materialTypeId: '',
  unitOfMeasure: '',
  owner: 'DRM',
  winterMaterial: false,
  vehicleRequiredForWithdrawal: false,
};

const FIELD_ORDER: MaterialField[] = ['code', 'description', 'materialTypeId', 'unitOfMeasure', 'owner'];

function fromMaterial(material: CatalogMaterial): MaterialPayload {
  return {
    code: material.code ?? '',
    description: material.description ?? '',
    materialTypeId: material.type?.id ?? '',
    // Units are lowercase (FMT-25); an old "EA" shows as "ea" when the material is corrected.
    unitOfMeasure: (material.unitOfMeasure ?? '').trim().toLowerCase(),
    owner: material.owner ?? '',
    winterMaterial: material.winterMaterial,
    vehicleRequiredForWithdrawal: material.vehicleRequiredForWithdrawal,
  };
}

/** What the browser can say before asking the server (FORM-06: the server says it again). */
function localErrors(form: MaterialPayload): Errors {
  const errors: Errors = {};
  if (!form.code.trim()) errors.code = 'Code required';
  if (!form.description.trim()) errors.description = 'Description required';
  if (!form.materialTypeId) errors.materialTypeId = 'Type required';
  if (!form.unitOfMeasure) errors.unitOfMeasure = 'Units of measure required';
  return errors;
}

const FieldError: React.FC<{ id: string; message?: string }> = ({ id, message }) =>
  message ? (
    <p id={id} className="tw-mb-0 tw-mt-1 tw-text-sm tw-text-danger" role="alert">
      {message}
    </p>
  ) : null;

/**
 * Add a material to the master list, or correct one (DLG-09: the title matches the action).
 * The code is checked for a clash as soon as the user leaves the field, so a duplicate is
 * caught before Save; the server checks again, and the database refuses one regardless.
 */
const MaterialFormModal: React.FC<MaterialFormModalProps> = ({ show, material, options, onHide, onSaved }) => {
  const editing = !!material;
  const [form, setForm] = useState<MaterialPayload>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const [codeClash, setCodeClash] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (!show) return;
    setForm(material ? fromMaterial(material) : EMPTY);
    setErrors({});
    setCodeClash(null);
    setFailure(null);
  }, [show, material]);

  const set = <K extends keyof MaterialPayload>(key: K, value: MaterialPayload[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (key in errors) setErrors((current) => ({ ...current, [key]: undefined }));
    if (key === 'code') setCodeClash(null);
  };

  const checkCode = async () => {
    const code = form.code.trim();
    if (!code || (editing && code.toLowerCase() === (material?.code ?? '').trim().toLowerCase())) return;
    try {
      const result = await materialCatalogService.codeAvailability(code, material?.id);
      setCodeClash(result.available ? null : result.message);
    } catch {
      // The save checks again; a failed early check must not block typing.
    }
  };

  const focusFirst = (found: Errors) => {
    const first = FIELD_ORDER.find((field) => found[field]);
    if (!first) return;
    const el = formRef.current?.querySelector<HTMLElement>(`[data-field="${first}"] input, [data-field="${first}"] select, [data-field="${first}"] button`);
    el?.scrollIntoView({ block: 'center' });
    el?.focus();
  };

  const save = async (event?: React.FormEvent) => {
    event?.preventDefault();
    const found = localErrors(form);
    if (codeClash && !found.code) found.code = codeClash;
    if (Object.keys(found).length) {
      setErrors(found);
      focusFirst(found);
      return;
    }
    setSaving(true);
    setFailure(null);
    try {
      const saved = editing ? await materialCatalogService.update(material!.id, form) : await materialCatalogService.create(form);
      onSaved(saved);
    } catch (error) {
      if (error instanceof CatalogRequestError && Object.keys(error.fields).length) {
        setErrors(error.fields);
        focusFirst(error.fields);
      } else {
        setFailure(error instanceof Error ? error.message : 'Could not save the material');
      }
    } finally {
      setSaving(false);
    }
  };

  const shownCodeError = errors.code ?? codeClash ?? undefined;
  const title = editing ? 'Edit Material' : 'Add Material';

  return (
    <Modal show={show} onHide={saving ? undefined : onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <form ref={formRef} onSubmit={save} noValidate>
        <Modal.Body>
          <div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
            <div data-field="code">
              <Form.Label htmlFor="material-code">
                Code <span className="tw-text-danger">*</span>
              </Form.Label>
              <Form.Control
                id="material-code"
                value={form.code}
                maxLength={40}
                autoComplete="off"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => set('code', event.target.value)}
                onBlur={() => void checkCode()}
                isInvalid={!!shownCodeError}
                aria-describedby={shownCodeError ? 'material-code-error' : 'material-code-help'}
              />
              {shownCodeError ? (
                <FieldError id="material-code-error" message={shownCodeError} />
              ) : (
                <p id="material-code-help" className="tw-mb-0 tw-mt-1 tw-text-xs tw-text-content-muted">
                  Unique across the catalogue. Case and spaces at either end don't make a code different.
                </p>
              )}
            </div>
            <div data-field="materialTypeId">
              <StandoutDropdown
                type="searchable"
                label="Type"
                isRequired
                options={options?.types ?? []}
                value={form.materialTypeId}
                onChange={(value) => set('materialTypeId', value)}
                getValue={(type: { id: string }) => type.id}
                formatOption={(type: { name: string }) => type.name}
                placeholder="Choose a Type"
              />
              <FieldError id="material-type-error" message={errors.materialTypeId} />
            </div>
            <div className="sm:tw-col-span-2" data-field="description">
              <Form.Label htmlFor="material-description">
                Description <span className="tw-text-danger">*</span>
              </Form.Label>
              <Form.Control
                id="material-description"
                value={form.description}
                maxLength={200}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => set('description', event.target.value)}
                isInvalid={!!errors.description}
                aria-describedby={errors.description ? 'material-description-error' : undefined}
              />
              <FieldError id="material-description-error" message={errors.description} />
            </div>
            <div data-field="unitOfMeasure">
              <Form.Label htmlFor="material-unit">
                Units of Measure <span className="tw-text-danger">*</span>
              </Form.Label>
              <Form.Select
                id="material-unit"
                value={form.unitOfMeasure}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => set('unitOfMeasure', event.target.value)}
                isInvalid={!!errors.unitOfMeasure}
                aria-describedby={errors.unitOfMeasure ? 'material-unit-error' : undefined}
              >
                <option value="">Choose Units of Measure</option>
                {(options?.units ?? []).map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </Form.Select>
              <FieldError id="material-unit-error" message={errors.unitOfMeasure} />
            </div>
            <div data-field="owner">
              <Form.Label htmlFor="material-owner">Owner</Form.Label>
              <Form.Select
                id="material-owner"
                value={form.owner}
                onChange={(event: React.ChangeEvent<HTMLSelectElement>) => set('owner', event.target.value)}
                isInvalid={!!errors.owner}
              >
                <option value="">No Owner</option>
                {(options?.owners ?? []).map((owner) => (
                  <option key={owner} value={owner}>
                    {owner}
                  </option>
                ))}
              </Form.Select>
              <FieldError id="material-owner-error" message={errors.owner} />
            </div>
            <div className="sm:tw-col-span-2 tw-flex tw-flex-col tw-gap-2">
              <Form.Check
                id="material-winter"
                type="checkbox"
                label="Winter Material"
                checked={form.winterMaterial}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => set('winterMaterial', event.target.checked)}
              />
              <Form.Check
                id="material-vehicle"
                type="checkbox"
                label="Vehicle Required for Withdrawal"
                checked={form.vehicleRequiredForWithdrawal}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => set('vehicleRequiredForWithdrawal', event.target.checked)}
              />
              <p className="tw-mb-0 tw-text-xs tw-text-content-muted">
                With this ticked, a crew taking the material must name the unit it went onto.
              </p>
            </div>
          </div>
          {failure ? (
            <p className="tw-mb-0 tw-mt-4 tw-text-sm tw-text-danger" role="alert">
              {failure}
            </p>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Material'}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default MaterialFormModal;
